import type { CannedWorkPackageStatus } from "@common/client"
import {
  CannedWorkPackageStatusSchema,
  SortDirectionSchema,
} from "@common/client/schemas.gen"
import { z } from "zod"
import type { Display } from "@/components/layout/display-options"
import type { ListSort } from "@/components/layout/list-table"
import type { ColumnSizing } from "@/lib/column-widths"
import { NUMBER_WIDTH, STATUS_WIDTH } from "./columns"

export const QUOTE_COLUMNS = [
  "number",
  "name",
  "applies_to",
  "tasks",
  "price",
  "created",
] as const

const quoteColumnSchema = z.enum(QUOTE_COLUMNS)

export type QuoteColumn = z.infer<typeof quoteColumnSchema>

export const FIXED_QUOTE_COLUMNS: readonly QuoteColumn[] = ["number", "name"]

const DEFAULT_QUOTE_COLUMNS: QuoteColumn[] = [...QUOTE_COLUMNS]

export const QUOTE_COLUMN_LABELS: Record<QuoteColumn, string> = {
  number: "Template no.",
  name: "Name",
  applies_to: "Applies to",
  tasks: "Tasks",
  price: "Price",
  created: "Created",
}

const QUOTE_SORTABLE = ["number", "name"] as const

const quoteSortSchema = z.enum(QUOTE_SORTABLE)

export type QuoteSort = z.infer<typeof quoteSortSchema>

export function isQuoteSortable(column: QuoteColumn): column is QuoteSort {
  return QUOTE_SORTABLE.some((sortable) => sortable === column)
}

export type QuoteSlot = QuoteColumn | "select" | "status" | "actions"

export const QUOTE_COLUMN_SIZING: ColumnSizing<QuoteSlot> = {
  id: "template-quotes",
  widths: {
    select: 2.25,
    status: STATUS_WIDTH,
    number: NUMBER_WIDTH,
    name: 16,
    applies_to: 12,
    tasks: 5,
    price: 8,
    created: 7,
    actions: 3.25,
  },
  fill: "name",
  pinned: ["select", "status", "actions"],
  extras: ["select", "status", "actions"],
  dropOrder: ["applies_to", "created", "tasks", "price"],
}

export function quoteSlotsOf(
  columns: QuoteColumn[],
  deletable: boolean,
): QuoteSlot[] {
  const rest = QUOTE_COLUMNS.filter(
    (column) => column !== "number" && columns.includes(column),
  )
  return deletable
    ? ["select", "number", "status", ...rest, "actions"]
    : ["number", "status", ...rest]
}

export const QUOTE_GROUPINGS = ["status"] as const

export type QuoteGrouping = (typeof QUOTE_GROUPINGS)[number]

export const QUOTE_GROUPING_LABELS: Record<QuoteGrouping, string> = {
  status: "Status",
}

export const QUOTE_STATUSES: CannedWorkPackageStatus[] = [
  "approved",
  "in_progress",
  "draft",
]

export function quoteStatusLabel(status: CannedWorkPackageStatus): string {
  switch (status) {
    case "approved":
      return "Approved"
    case "in_progress":
      return "In progress"
    case "draft":
      return "Draft"
  }
}

export const quoteFilterSchema = z.object({
  archived: z.boolean().optional().catch(false),
  search: z.string().optional().catch(undefined),
  aircraft: z.array(z.string()).optional().catch(undefined),
  status: z
    .array(z.enum(CannedWorkPackageStatusSchema.enum))
    .optional()
    .catch(undefined),
  group: z.enum(QUOTE_GROUPINGS).optional().catch(undefined),
  columns: z.array(quoteColumnSchema).optional().catch(undefined),
  sort: quoteSortSchema.optional().catch(undefined),
  direction: z.enum(SortDirectionSchema.enum).optional().catch(undefined),
})

export type QuoteFilters = z.infer<typeof quoteFilterSchema>

export const NO_QUOTE_FILTERS: Pick<QuoteFilters, "aircraft" | "status"> = {
  aircraft: undefined,
  status: undefined,
}

export function hasQuoteFilters(filters: QuoteFilters): boolean {
  return Boolean(filters.aircraft?.length || filters.status?.length)
}

export function quoteListParams(filters: QuoteFilters) {
  return {
    archived: filters.archived ?? false,
    search: filters.search || null,
    type_certificate_model_variant_id: filters.aircraft?.length
      ? filters.aircraft
      : null,
    status: filters.status?.length ? filters.status : null,
    sort_by: filters.sort ?? "number",
    direction: filters.direction ?? "asc",
  }
}

export function quoteDisplay(filters: QuoteFilters): Display<QuoteGrouping> {
  return { group: filters.group, subgroup: undefined }
}

export function quoteDisplaySearch(
  next: Display<QuoteGrouping>,
): Pick<QuoteFilters, "group"> {
  return { group: next.group }
}

export function quoteGroupParams(filters: QuoteFilters, group: QuoteGrouping) {
  return { ...quoteListParams(filters), group }
}

export function quoteColumns(filters: QuoteFilters): QuoteColumn[] {
  return filters.columns
    ? QUOTE_COLUMNS.filter(
        (column) =>
          FIXED_QUOTE_COLUMNS.includes(column) ||
          filters.columns?.includes(column),
      )
    : DEFAULT_QUOTE_COLUMNS
}

export function quoteSort(filters: QuoteFilters): ListSort<QuoteSort> {
  return {
    column: filters.sort ?? "number",
    direction: filters.direction ?? "asc",
  }
}

export function quoteSortSearch(
  filters: QuoteFilters,
  column: QuoteSort,
): Pick<QuoteFilters, "sort" | "direction"> {
  const current = quoteSort(filters)
  const direction =
    column === current.column && current.direction === "asc" ? "desc" : "asc"
  return column === "number" && direction === "asc"
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

export function quoteColumnsSearch(
  columns: QuoteColumn[],
): Pick<QuoteFilters, "columns"> {
  const defaults =
    columns.length === DEFAULT_QUOTE_COLUMNS.length &&
    columns.every((column) => DEFAULT_QUOTE_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}
